import { useState, useRef, useEffect, useCallback } from 'react';

interface AudioPlayerProps {
  previewUrl: string;
  previewDuration: string;
  label?: string;
}

export default function AudioPlayer({ previewUrl, previewDuration, label = 'Ouça uma prévia grátis' }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressRef = useRef<number | null>(null);
  const bars = 16;

  useEffect(() => {
    const audio = new Audio(previewUrl);
    audio.preload = 'metadata';
    audioRef.current = audio;

    const onLoaded = () => {
      setDuration(audio.duration || 113);
    };

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration > 0) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    const onEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      if (progressRef.current) cancelAnimationFrame(progressRef.current);
    };
  }, [previewUrl]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percent * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(percent * 100);
  }, [duration]);

  const formatTime = (time: number) => {
    if (!time || isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="rounded-xl p-4 border bg-gradient-to-br from-primary-50 via-accent-50/70 to-primary-50 border-primary-200/60 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 shadow-sm shadow-primary-500/30">
          <i className="ri-headphone-line text-background-50 text-xs" />
        </div>
        <span className="text-[11px] font-extrabold text-primary-700 uppercase tracking-[0.15em]">{label}</span>
      </div>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={togglePlay}
          className="relative w-14 h-14 flex items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all cursor-pointer flex-shrink-0 shadow-lg shadow-primary-500/40 hover:shadow-primary-500/50 hover:scale-105 active:scale-95"
          aria-label={isPlaying ? 'Pausar prévia' : 'Ouvir prévia'}
        >
          {isPlaying && (
            <span className="absolute inset-0 rounded-full bg-primary-500 animate-ping opacity-20" />
          )}
          <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-2xl`} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-end gap-[3px] h-7 mb-2">
            {Array.from({ length: bars }).map((_, i) => {
              const active = isPlaying && (i / bars) * 100 <= progress;
              const randomHeight = 20 + (i % 5) * 15;
              return (
                <div
                  key={i}
                  className={`flex-1 rounded-full transition-all duration-300 ease-in-out ${
                    active ? 'bg-primary-500' : 'bg-primary-300/60'
                  }`}
                  style={{
                    height: isPlaying ? `${randomHeight + Math.sin(Date.now() / 200 + i) * 10}px` : `${randomHeight * 0.4}px`,
                    animation: isPlaying ? `float ${0.8 + (i % 3) * 0.3}s ease-in-out infinite` : 'none',
                    animationDelay: `${i * 0.05}s`,
                  }}
                />
              );
            })}
          </div>
          <div
            className="h-1.5 bg-background-300/50 rounded-full overflow-hidden cursor-pointer group/progress"
            onClick={handleProgressClick}
            role="slider"
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={0}
            onKeyDown={(e) => {
              if (!audioRef.current || !duration) return;
              const step = duration * 0.05;
              if (e.key === 'ArrowLeft') {
                const newTime = Math.max(0, currentTime - step);
                audioRef.current.currentTime = newTime;
              } else if (e.key === 'ArrowRight') {
                const newTime = Math.min(duration, currentTime + step);
                audioRef.current.currentTime = newTime;
              }
            }}
          >
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-200 relative"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[11px] font-semibold text-foreground-700 tabular-nums">
              {isPlaying ? formatTime(currentTime) : 'Toque para ouvir'}
            </span>
            <span className="text-[11px] text-foreground-500 tabular-nums">{previewDuration}</span>
          </div>
        </div>
      </div>
    </div>
  );
}